import { throttle } from 'lodash'
import { Map } from 'ms-map-gl'
import { FC, memo } from 'react'
import { useDispatch } from 'react-redux'
import { useIsMobile } from 'ui-components'

import { Marker } from './Marker'
import { ClusterSource } from './Sources/ClusterSource'
import { GeojsonSource } from './Sources/GeojsonSource'
import { H3Source } from './Sources/H3Source'

import { useGeojsonData } from '~map-preview/hooks/useGeojsonData'
import { useIsAllToolsHidden } from '~map-preview/states/ui'
import { useFilterSelectionSet } from './handlers/filterSelectionSetHandler'
import { usePmtiles } from './handlers/usePmtiles'
import { useSetFitBounds } from './handlers/useSetFitBounds'

import { LAYER_TYPES, useLayerType } from '~map-preview/states/layerType'
import { useMapId } from '~map-preview/states/map'
import { MARKER_TYPES, useMarkerType } from '~map-preview/states/marker'
import { setViewState, useStyle } from '~map-preview/states/viewState'
import { useInitialViewState } from './handlers/useInitialViewState'
import { PMTilesSource } from './Sources'

type Props = {
  children?: React.ReactNode
}

const MapGl: FC<Props> = memo((props) => {
  const { children } = props

  const dispatch = useDispatch()

  const mapId = useMapId()
  const isAllToolsHidden = useIsAllToolsHidden()
  const isMobile = useIsMobile()

  const layerType = useLayerType()
  const markerType = useMarkerType()

  const geojson = useGeojsonData()
  const style = useStyle()

  usePmtiles()
  useFilterSelectionSet()
  useSetFitBounds()

  const initialViewState = useInitialViewState()

  const handleMove = throttle((event) => {
    const viewState = event.viewState
    dispatch(setViewState(viewState))
  }, 1000)

  return (
    <Map
      // ::note::
      // key set to mapId to force rerendering of the map when the mapId changes
      key={mapId}
      style={style}
      initialViewState={initialViewState}
      navigationControl={!isMobile && !isAllToolsHidden}
      attributionControl={!isMobile && !isAllToolsHidden}
      dragRotate
      onMoveEnd={handleMove}
    >
      <GeojsonSource geojson={geojson} />

      {layerType === LAYER_TYPES.HEXAGON && <H3Source geojson={geojson} />}

      {layerType === LAYER_TYPES.MARKER &&
        markerType === MARKER_TYPES.CLUSTERS && (
          <ClusterSource geojson={geojson} />
        )}

      {layerType === LAYER_TYPES.BOUNDARIES && (
        <PMTilesSource geojson={geojson} />
      )}

      <Marker />

      {children}
    </Map>
  )
})

export { MapGl }
